/**
 * A handful of routes doesn't need a routing library.
 *
 * Patterns use `:name` segments and match the whole path. History is driven
 * through pushState with a popstate listener, so back and forward work and
 * deep links survive a reload — the server serves index.html for any path.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

export function usePath(): string {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    window.addEventListener("app:navigate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("app:navigate", onPop);
    };
  }, []);

  return path;
}

export function navigate(to: string, replace = false): void {
  if (replace) window.history.replaceState({}, "", to);
  else window.history.pushState({}, "", to);
  window.dispatchEvent(new Event("app:navigate"));
  window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
}

/** Matches `/match/:id/:token` style patterns, returning captured params. */
export function matchRoute(
  pattern: string,
  path: string,
): Record<string, string> | null {
  const patternParts = pattern.split("/").filter(Boolean);
  const pathParts = path.split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i++) {
    const p = patternParts[i]!;
    const v = pathParts[i]!;
    if (p.startsWith(":")) params[p.slice(1)] = decodeURIComponent(v);
    else if (p !== v) return null;
  }
  return params;
}

export function useRoute<T extends string>(
  patterns: readonly T[],
): { pattern: T | null; params: Record<string, string>; path: string } {
  const path = usePath();
  return useMemo(() => {
    for (const pattern of patterns) {
      const params = matchRoute(pattern, path);
      if (params) return { pattern, params, path };
    }
    return { pattern: null, params: {}, path };
  }, [path, patterns]);
}

export function useQuery(): URLSearchParams {
  const path = usePath();
  return useMemo(
    () => new URLSearchParams(window.location.search),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path],
  );
}

export function Link({
  href,
  children,
  className,
  ...rest
}: React.ComponentProps<"a"> & { href: string }) {
  const onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      navigate(href);
    },
    [href],
  );

  return (
    <a href={href} onClick={onClick} className={className} {...rest}>
      {children}
    </a>
  );
}
